import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

import { execMeetingAPI } from '../../api/client';
import { COLORS } from '../../utils/uiStyles';

const CANONICAL_IMAGE_PREFIX = '/api/exec-meeting/img/';
const RICH_CLASS = 'gw-exec-rich';
const RICH_STYLE_ID = 'gw-exec-rich-style';
const RICH_LIST_CSS = `.${RICH_CLASS} ul, .${RICH_CLASS} ol { padding-left: 2em; margin: 4px 0; } .${RICH_CLASS} li { margin: 2px 0; }`;
const MAX_IMAGE_REQUESTS = 4;
let activeImageRequests = 0;
const pendingImageRequests = [];

// index.html 전역 `* { padding:0 }` 가 ol/ul 들여쓰기를 0 으로 만들어 번호·글머리가 칸 왼쪽 밖으로 잘린다 —
// 본문 칸(편집·읽기·인쇄)에만 되살린다. 화면 표시용이라 저장 HTML 에는 섞이지 않는다. 한 번 붙이면 지우지 않는다.
function ensureRichListStyle() {
  if (typeof document === 'undefined' || document.getElementById(RICH_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = RICH_STYLE_ID;
  style.textContent = RICH_LIST_CSS;
  document.head.appendChild(style);
}

function drainImageRequests() {
  while (activeImageRequests < MAX_IMAGE_REQUESTS && pendingImageRequests.length) {
    const request = pendingImageRequests.shift();
    if (request.cancelled()) {
      request.resolve(null);
      continue;
    }
    activeImageRequests += 1;
    execMeetingAPI.image(request.uuid)
      .then((response) => request.resolve(response.data))
      .catch(() => request.resolve(null))
      .finally(() => {
        activeImageRequests -= 1;
        drainImageRequests();
      });
  }
}

function loadProtectedImage(uuid, cancelled) {
  return new Promise((resolve) => {
    pendingImageRequests.push({ uuid, cancelled, resolve });
    drainImageRequests();
  });
}

function imageUuid(src) {
  if (!src || !src.startsWith(CANONICAL_IMAGE_PREFIX)) return null;
  const value = src.slice(CANONICAL_IMAGE_PREFIX.length).split(/[?#]/, 1)[0];
  try {
    return value ? decodeURIComponent(value) : null;
  } catch {
    return null;
  }
}

async function prepareInitialHTML(initialHTML, objectURLs, cancelled) {
  const doc = new DOMParser().parseFromString(`<div>${initialHTML || ''}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  const images = Array.from(root.querySelectorAll('img'))
    .map((img) => ({ img, uuid: imageUuid(img.getAttribute('src')) }))
    .filter(({ uuid }) => uuid);
  const uuids = [...new Set(images.map(({ uuid }) => uuid))];
  const loaded = new Map(await Promise.all(uuids.map(async (uuid) => (
    [uuid, await loadProtectedImage(uuid, cancelled)]
  ))));

  if (cancelled()) return '';
  const blobURLs = new Map();
  loaded.forEach((blob, uuid) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    objectURLs.add(url);
    blobURLs.set(uuid, url);
  });
  images.forEach(({ img, uuid }) => {
    img.dataset.imgUuid = uuid;
    const blobURL = blobURLs.get(uuid);
    if (blobURL) {
      img.setAttribute('src', blobURL);
      return;
    }
    img.removeAttribute('src');
    img.dataset.imgLoadFailed = 'true';
    const error = doc.createElement('span');
    error.dataset.imgLoadError = 'true';
    error.textContent = '이미지 불러오기 실패';
    img.insertAdjacentElement('afterend', error);
  });
  return root.innerHTML;
}

export function RichViewer({ initialHTML = '', ariaLabel }) {
  const objectURLsRef = useRef(new Set());
  const [preparedHTML, setPreparedHTML] = useState(null);

  useLayoutEffect(() => {
    ensureRichListStyle();
  }, []);

  useEffect(() => {
    let stopped = false;
    setPreparedHTML(null);
    prepareInitialHTML(initialHTML, objectURLsRef.current, () => stopped)
      .then((html) => {
        if (!stopped) setPreparedHTML(html);
      });
    return () => {
      stopped = true;
      objectURLsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectURLsRef.current.clear();
    };
  }, [initialHTML]);

  if (preparedHTML === null) {
    return <div role="status" style={{ color: `var(--gw-secondary, ${COLORS.textSub})`, fontSize: '13px' }}>본문을 불러오는 중입니다.</div>;
  }

  if (!preparedHTML) {
    return <div aria-label={ariaLabel} style={{ color: `var(--gw-muted, ${COLORS.textMuted})`, fontSize: '13px' }}>등록된 내용이 없습니다.</div>;
  }

  return (
    <div
      aria-label={ariaLabel}
      className={RICH_CLASS}
      dangerouslySetInnerHTML={{ __html: preparedHTML }}
      style={{ lineHeight: 1.6, color: `var(--gw-text, ${COLORS.text})`, fontSize: '14px', overflowWrap: 'anywhere' }}
    />
  );
}

function loadBrowserImage(file) {
  return new Promise((resolve, reject) => {
    const sourceURL = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(sourceURL);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(sourceURL);
      reject(new Error('이미지를 읽을 수 없습니다.'));
    };
    image.src = sourceURL;
  });
}

async function compressImage(file) {
  const image = await loadBrowserImage(file);
  const scale = Math.min(1, 1000 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('이미지를 처리할 수 없습니다.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (value) => (value ? resolve(value) : reject(new Error('이미지를 압축할 수 없습니다.'))),
      'image/jpeg',
      0.72,
    );
  });
  const baseName = (file.name || 'pasted-image').replace(/\.[^.]*$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

function insertAtSelection(editor, node) {
  const selection = window.getSelection();
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
  if (range && editor.contains(range.commonAncestorContainer)) {
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    editor.appendChild(node);
  }
}

// 화면 문서에 속한 요소에 넣으면 src 를 바꾸는 순간 브라우저가 인증 없이 불러 403 이 난다(#3142) — 비활성 문서에서 정규화한다.
function normalizedHTML(editor) {
  const doc = document.implementation.createHTMLDocument('');
  const copy = doc.createElement('div');
  copy.innerHTML = editor?.innerHTML || '';
  copy.querySelectorAll('[data-upload-placeholder], [data-upload-error], [data-img-load-error]')
    .forEach((node) => node.remove());
  copy.querySelectorAll('img[data-img-uuid]').forEach((img) => {
    const uuid = img.dataset.imgUuid;
    if (!uuid) return;
    img.setAttribute('src', `${CANONICAL_IMAGE_PREFIX}${encodeURIComponent(uuid)}`);
    img.removeAttribute('data-img-uuid');
    img.removeAttribute('data-img-load-failed');
  });
  return copy.innerHTML;
}

const toolbarButton = {
  border: `1px solid var(--gw-border, ${COLORS.border})`,
  borderRadius: '5px',
  background: `var(--gw-surface, ${COLORS.surface})`,
  color: `var(--gw-text, ${COLORS.text})`,
  minWidth: '30px',
  height: '28px',
  cursor: 'pointer',
  fontSize: '12px',
};

const UncontrolledEditorSurface = memo(forwardRef(function UncontrolledEditorSurface({ initialHTML, onPaste }, ref) {
  return (
    <div
      ref={ref}
      className={RICH_CLASS}
      contentEditable
      suppressContentEditableWarning
      onPaste={onPaste}
      dangerouslySetInnerHTML={{ __html: initialHTML }}
      style={{ minHeight: '110px', padding: '10px 12px', outline: 'none', lineHeight: 1.6, fontSize: '14px' }}
    />
  );
}));

const RichEditor = forwardRef(function RichEditor({ field, initialHTML = '', disabled = false, onReady, onPendingChange }, ref) {
  const editorRef = useRef(null);
  const disabledRef = useRef(disabled);
  const objectURLsRef = useRef(new Set());
  const pendingUploadsRef = useRef(new Set());
  const preparationRef = useRef(Promise.resolve());
  const resolvePreparationRef = useRef(null);
  const [preparedHTML, setPreparedHTML] = useState(null);

  disabledRef.current = disabled;

  useLayoutEffect(() => {
    ensureRichListStyle();
  }, []);

  useEffect(() => {
    let stopped = false;
    setPreparedHTML(null);
    onReady?.(field, false);
    preparationRef.current = new Promise((resolve) => {
      resolvePreparationRef.current = resolve;
    });
    prepareInitialHTML(initialHTML, objectURLsRef.current, () => stopped)
      .then((html) => {
        if (!stopped) setPreparedHTML(html);
      });
    return () => {
      stopped = true;
      resolvePreparationRef.current?.();
      resolvePreparationRef.current = null;
      onReady?.(field, false);
      objectURLsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectURLsRef.current.clear();
    };
  }, [field, initialHTML, onReady]);

  useLayoutEffect(() => {
    if (preparedHTML === null || !editorRef.current) return;
    editorRef.current.contentEditable = disabled ? 'false' : 'true';
    resolvePreparationRef.current?.();
    resolvePreparationRef.current = null;
    onReady?.(field, true);
  }, [disabled, field, onReady, preparedHTML]);

  useImperativeHandle(ref, () => ({
    async getHTML() {
      await preparationRef.current;
      while (pendingUploadsRef.current.size) {
        await Promise.allSettled([...pendingUploadsRef.current]);
      }
      return normalizedHTML(editorRef.current);
    },
    isReady() {
      return preparedHTML !== null && Boolean(editorRef.current);
    },
    hasPendingUploads() {
      return pendingUploadsRef.current.size > 0;
    },
  }), [preparedHTML]);

  const runCommand = (command) => (event) => {
    event.preventDefault();
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false);
  };

  const uploadPastedImage = useCallback(async (file, placeholder) => {
    try {
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append('file', compressed);
      form.append('field', field);
      const response = await execMeetingAPI.uploadImage(form);
      const uuid = response.data?.uuid;
      if (!uuid) throw new Error('이미지 식별자가 없습니다.');
      if (!placeholder.isConnected) return;
      const image = document.createElement('img');
      const blobURL = URL.createObjectURL(compressed);
      objectURLsRef.current.add(blobURL);
      image.src = blobURL;
      image.dataset.imgUuid = uuid;
      image.alt = file.name || '붙여넣은 이미지';
      placeholder.replaceWith(image);
    } catch (err) {
      console.warn('[execmeeting] 이미지 업로드 실패', err?.response?.status ?? err?.message);
      if (!placeholder.isConnected) return;
      const error = document.createElement('span');
      error.dataset.uploadError = 'true';
      error.textContent = '이미지 업로드 실패';
      placeholder.replaceWith(error);
    }
  }, [field]);

  const handlePaste = useCallback((event) => {
    if (disabledRef.current) return;
    const files = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (!files.length) return;
    event.preventDefault();
    files.forEach((file) => {
      const placeholder = document.createElement('span');
      placeholder.dataset.uploadPlaceholder = 'true';
      placeholder.textContent = '이미지 업로드 중…';
      insertAtSelection(editorRef.current, placeholder);
      let task;
      task = uploadPastedImage(file, placeholder).finally(() => {
        pendingUploadsRef.current.delete(task);
        onPendingChange?.(field, pendingUploadsRef.current.size);
      });
      pendingUploadsRef.current.add(task);
      onPendingChange?.(field, pendingUploadsRef.current.size);
    });
  }, [field, onPendingChange, uploadPastedImage]);

  if (preparedHTML === null) {
    return <div role="status" style={{ padding: '14px', color: `var(--gw-secondary, ${COLORS.textSub})`, fontSize: '13px' }}>본문을 불러오는 중입니다.</div>;
  }

  return (
    <div style={{ border: `1px solid var(--gw-border, ${COLORS.border})`, borderRadius: '8px', overflow: 'hidden', background: `var(--gw-surface, ${COLORS.surface})` }}>
      <div style={{ display: 'flex', gap: '5px', padding: '6px', borderBottom: `1px solid var(--gw-line, ${COLORS.borderSoft})`, background: 'var(--gw-subtle, #fbfbfa)' }}>
        <button type="button" aria-label="굵게" disabled={disabled} onMouseDown={runCommand('bold')} style={toolbarButton}><b>B</b></button>
        <button type="button" aria-label="기울임" disabled={disabled} onMouseDown={runCommand('italic')} style={toolbarButton}><i>I</i></button>
        <button type="button" aria-label="밑줄" disabled={disabled} onMouseDown={runCommand('underline')} style={toolbarButton}><u>U</u></button>
        <button type="button" aria-label="글머리 목록" disabled={disabled} onMouseDown={runCommand('insertUnorderedList')} style={toolbarButton}>• 목록</button>
        <button type="button" aria-label="번호 목록" disabled={disabled} onMouseDown={runCommand('insertOrderedList')} style={toolbarButton}>1. 목록</button>
      </div>
      <UncontrolledEditorSurface
        ref={editorRef}
        initialHTML={preparedHTML}
        onPaste={handlePaste}
      />
    </div>
  );
});

export default RichEditor;
